import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHead from "../components/shared/SEOHead.jsx";
import JsonLd from "../components/shared/JsonLd.jsx";
import FaqAccordion from "../components/shared/FaqAccordion.jsx";
import Breadcrumbs from "../components/shared/Breadcrumbs.jsx";
import { buildBreadcrumbJsonLd, buildFaqJsonLd } from "../utils/jsonld.js";
import { ABOUT_PATH, ORGAS_PATH, SUBPAGE_PATH } from "./About/paths.js";
import { SUPPORT_PATH, SUPPORT_SUBPAGE_PATH } from "./Support/paths.js";

export const JUGENDARBEIT_PATH = { de: "jugendarbeit", en: "youth-work" };

const LARP_PATH = { de: "larp-erleben", en: "experience-larp" };
const WAS_IST_LARP_SLUG = { de: "was-ist-larp", en: "what-is-larp" };
const KONTAKT_PATH = { de: "kontakt", en: "contact" };
const SPENDEN_PATH = { de: "spenden", en: "donate" };
const FREIPLATZ_SLUG = { de: "freiplatz-fonds-2026", en: "free-ticket-fund-2026" };

const bodyStyle = { fontSize: "16px", lineHeight: 1.7, color: "rgba(10,9,7,0.85)", margin: 0 };
const sectionTitleStyle = { fontFamily: "var(--font-serif)", fontSize: "22px", color: "var(--color-ink)", margin: 0 };
const offerTitleStyle = { fontFamily: "var(--font-serif)", fontSize: "18px", color: "var(--color-ink)", margin: 0 };

function Section({ title, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <p style={sectionTitleStyle}>{title}</p>
      {children}
    </div>
  );
}

function Offer({ title, body }) {
  return (
    <div className="tl-card-light" style={{ display: "flex", flexDirection: "column", gap: "8px", padding: "24px 26px" }}>
      <p style={offerTitleStyle}>{title}</p>
      <p style={bodyStyle}>{body}</p>
    </div>
  );
}

/**
 * "Unsere Angebote" items are kept close to what's already documented
 * elsewhere on the site (VolunteerContent's "Teamer für Jugend-LARP" role
 * mentions Tagesevents and Ferienangebote as real; the Jugendschutzkonzept's
 * Anhang D, a "Checkliste für Jugendevents", confirms these events already
 * happen). School/youth-centre partnerships are a communicated offer, not
 * yet an existing cooperation — worded accordingly below.
 */
const OFFERS = {
  de: [
    { title: "Tagesevents für Jugendgruppen", body: "Ein Nachmittag oder Tag Live-Rollenspiel für eine bestehende Jugendgruppe — mit angepasstem Setting und Regelwerk." },
    { title: "Ferien-LARP", body: "Mehrtägige Angebote in den Schulferien, mit Übernachtung und vollem Programm." },
    { title: "Kooperationen mit Schulen und Jugendhäusern", body: "Projekttage oder wiederkehrende Angebote in Zusammenarbeit mit Einrichtungen — ein Angebot, das wir gern mit interessierten Schulen und Jugendhäusern gemeinsam aufbauen." },
    { title: "Jugendliche als NSC und Junior-Teamer", body: "Ältere Jugendliche können bei Erwachsenen-Events als NSC mitspielen oder in die Teamer-Rolle hineinwachsen." },
  ],
  en: [
    { title: "Day events for youth groups", body: "An afternoon or a day of live-action roleplay for an existing youth group — with an adapted setting and ruleset." },
    { title: "Holiday LARP", body: "Multi-day programmes during school holidays, with overnight stays and a full schedule." },
    { title: "Partnerships with schools and youth centres", body: "Project days or recurring programmes run together with institutions — an offer we're happy to build together with interested schools and youth centres." },
    { title: "Young people as NPCs and junior supervisors", body: "Older teens can play NPCs at adult events or grow into a supervisor role." },
  ],
};

const FAQ = {
  de: [
    { question: "Ab welchem Alter?", answer: "Das hängt vom Format ab und wird auf der jeweiligen Event-Seite kommuniziert. Unsere Jugend-LARPs richten sich aber vor allem an 8- bis 18-Jährige." },
    { question: "Muss mein Kind Fantasy mögen?", answer: "Nein. Viele Settings funktionieren auch ohne Magie und Monster — worum es wirklich geht, ist Zusammenspiel und Geschichte." },
    { question: "Was, wenn es Kampf im Spiel gibt?", answer: "Kampf wird ausschließlich mit weichen Schaumstoffwaffen und klaren Regeln gespielt; wer nicht kämpfen will, muss es nicht." },
    { question: "Gibt es Freiplätze?", answer: "Ja, über unseren Freiplatz-Fonds — diskret, ohne Bedürftigkeitsprüfung." },
  ],
  en: [
    { question: "What age is this for?", answer: "It depends on the format and is communicated on the individual event page. Our youth LARPs are aimed mainly at 8- to 18-year-olds." },
    { question: "Does my child need to like fantasy?", answer: "No. Many settings work without magic or monsters — what really matters is playing together and the story." },
    { question: "What if there's combat in the game?", answer: "Combat is played only with soft foam weapons and clear rules; nobody who doesn't want to fight has to." },
    { question: "Are there free places?", answer: "Yes, through our free-ticket fund — discreetly, with no means-testing." },
  ],
};

export default function Jugendarbeit({ lang }) {
  const { t } = useTranslation();
  const otherLang = lang === "de" ? "en" : "de";
  const path = `/${JUGENDARBEIT_PATH[lang]}`;
  const altPath = `/${JUGENDARBEIT_PATH[otherLang]}`;
  const jugendschutzPath = `/${lang}/${ABOUT_PATH[lang]}/${SUBPAGE_PATH.jugendschutz[lang]}`;
  const ehrenamtPath = `/${lang}/${SUPPORT_PATH[lang]}/${SUPPORT_SUBPAGE_PATH.volunteer[lang]}`;
  const eventsPath = `/${lang}/events`;
  const wasIstLarpPath = `/${lang}/${LARP_PATH[lang]}/${WAS_IST_LARP_SLUG[lang]}`;
  const fuerOrgasPath = `/${lang}/${ABOUT_PATH[lang]}/${ORGAS_PATH[lang]}`;
  const kontaktPath = `/${lang}/${KONTAKT_PATH[lang]}`;

  const title = lang === "de" ? "Jugend-LARP & Jugendarbeit in Sachsen" : "Youth LARP & Youth Work in Saxony";
  const description =
    lang === "de"
      ? "Live-Rollenspiel für Jugendliche in Dresden und Sachsen: Tagesevents, Ferienangebote und Kooperationen mit Jugendhäusern und Schulen."
      : "Live-action roleplay for young people in Dresden and Saxony: day events, holiday programmes and partnerships with youth centres and schools.";
  const h1 = lang === "de" ? "Jugend-LARP: Rollenspiel, das Jugendliche wachsen lässt" : "Youth LARP: Roleplay That Helps Young People Grow";

  const breadcrumbItems = [
    { name: t("nav.home"), path: "/" },
    { name: t("nav.ueberUns"), path: `/${ABOUT_PATH[lang]}` },
    { name: title, path },
  ];

  return (
    <>
      <SEOHead lang={lang} path={path} altPath={altPath} title={title} description={description} />
      <JsonLd data={buildBreadcrumbJsonLd(breadcrumbItems, lang)} />
      <JsonLd data={buildFaqJsonLd(FAQ[lang].map(({ question, answer }) => ({ question, answer })))} />

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div className="tl-container" style={{ display: "flex", flexDirection: "column", gap: "16px", paddingTop: "32px", paddingBottom: "96px", maxWidth: "900px" }}>
          <Breadcrumbs items={breadcrumbItems} lang={lang} />
          <h1 className="tl-hero-h1" style={{ fontFamily: "var(--font-serif)", fontSize: "34px", lineHeight: 1.15, color: "var(--color-cream)", margin: 0 }}>
            {h1}
          </h1>
          <div className="tl-card-light tl-simple-card" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "36px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {lang === "de" ? (
              <>
                <p style={bodyStyle}>
                  Jugend-LARP ist Live-Rollenspiel, das gezielt für Jugendliche gestaltet ist: eigene Settings,
                  angepasste Regeln, geschulte Betreuung. Statt einer Geschichte zuzusehen, gestalten die
                  Teilnehmenden sie selbst — als Figur, die sie sich ausgedacht haben, in einer Gruppe, die
                  gemeinsam entscheidet, wie es weitergeht.
                </p>
                <p style={bodyStyle}>
                  Was dabei entsteht, ist mehr als ein Wochenende Spaß: Wer eine Figur durch schwierige
                  Entscheidungen spielt, erlebt Selbstwirksamkeit. Wer eine ganz andere Rolle einnimmt, übt
                  Perspektivwechsel. Wer mit einer Gruppe ein Lager aufbaut, lernt Teamarbeit fast nebenbei. Und
                  weil Konflikte im Spiel passieren, nicht im echten Leben, ist LARP ein geschützter Raum, um
                  Konfliktlösung auszuprobieren.
                </p>
              </>
            ) : (
              <>
                <p style={bodyStyle}>
                  Youth LARP is live-action roleplay designed specifically for young people: its own settings,
                  adapted rules, trained supervision. Instead of watching a story, participants shape it themselves
                  — as a character they invented, in a group that decides together what happens next.
                </p>
                <p style={bodyStyle}>
                  What comes out of that is more than a fun weekend: playing a character through difficult
                  decisions builds a sense of agency. Taking on a very different role practises perspective-taking.
                  Building a camp with a group teaches teamwork almost by accident. And because conflict happens in
                  the game, not in real life, LARP is a protected space to practise resolving it.
                </p>
              </>
            )}
          </div>

          <Section title={lang === "de" ? "Unsere Angebote" : "Our Programmes"}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))", gap: "18px" }}>
              {OFFERS[lang].map((offer) => (
                <Offer key={offer.title} {...offer} />
              ))}
            </div>
          </Section>

          <Section title={lang === "de" ? "Sicherheit und Jugendschutz" : "Safety and Youth Protection"}>
            <p style={bodyStyle}>
              {lang === "de" ? (
                <>
                  Erweitertes Führungszeugnis für alle, die mit Jugendlichen arbeiten, Safety-Tools wie die X-Card
                  und das Brake-System, und volle Information für Eltern vor jeder Anmeldung. Alles Weitere steht in
                  unserem{" "}
                  <Link to={jugendschutzPath}>Jugendschutzkonzept</Link>.
                </>
              ) : (
                <>
                  An extended background check for everyone working with young people, safety tools like the X-Card
                  and the brake system, and full information for parents before every sign-up. Everything else is
                  in our{" "}
                  <Link to={jugendschutzPath}>youth protection concept</Link>.
                </>
              )}
            </p>
          </Section>

          <Section title={lang === "de" ? "Für Eltern" : "For Parents"}>
            <p style={bodyStyle}>
              {lang === "de"
                ? "Vor jeder Anmeldung bekommt ihr das komplette Programm, wer betreut, unser Jugendschutzkonzept und die Safety-Tools schriftlich. Unter 16 ist die schriftliche Einverständniserklärung der Erziehungsberechtigten Pflicht. Was ein Tag oder ein Wochenende kostet, steht bei der jeweiligen Ausschreibung — wer sich das nicht leisten kann, fragt nach einem Freiplatz."
                : "Before every sign-up you get the full programme, who's supervising, our youth protection concept, and the safety tools in writing. Under-16s require written parental consent. What a day or weekend costs is listed with the specific announcement — if you can't afford it, ask about a free ticket."}
              {" "}
              <Link to={`/${lang}/${SPENDEN_PATH[lang]}/${FREIPLATZ_SLUG[lang]}`}>
                {lang === "de" ? "Freiplatz-Fonds" : "Free-ticket fund"}
              </Link>
              .
            </p>
          </Section>

          <Section title={lang === "de" ? "Für Fachkräfte und Einrichtungen" : "For Professionals and Institutions"}>
            <p style={bodyStyle}>
              {lang === "de" ? (
                <>
                  Du arbeitest in einer Schule, einem Jugendhaus oder einem Verein und willst LARP als Format
                  nutzen? Sprich uns an — wir klären gemeinsam Ablauf, Betreuungsschlüssel und Kosten. Wenn ihr
                  selbst ein größeres Event plant, aber keinen rechtlichen Rahmen habt, hilft auch unsere Seite für{" "}
                  <Link to={fuerOrgasPath}>Orgas &amp; Con-Teams</Link>.
                </>
              ) : (
                <>
                  Do you work at a school, youth centre, or association and want to use LARP as a format? Get in
                  touch — we'll work out the schedule, staffing ratio, and cost together. If you're planning a
                  bigger event yourselves but don't have a legal sponsor, our page for{" "}
                  <Link to={fuerOrgasPath}>organisers &amp; con teams</Link> can help too.
                </>
              )}
            </p>
          </Section>

          <Section title={lang === "de" ? "Häufige Fragen" : "Frequently Asked Questions"}>
            <FaqAccordion items={FAQ[lang].map(({ question, answer }) => ({ question, answer }))} />
          </Section>

          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
            <Link to={kontaktPath} className="tl-pill tl-pill-dark" style={{ padding: "13px 26px" }}>
              {lang === "de" ? "Anfrage für Einrichtungen" : "Enquiry for institutions"}
            </Link>
            <Link to={`/${lang}/newsletter`} className="tl-pill tl-pill-outline" style={{ padding: "13px 26px" }}>
              {lang === "de" ? "Newsletter für Eltern" : "Newsletter for parents"}
            </Link>
          </div>

          <p style={{ fontSize: "13px", color: "rgba(10,9,7,0.6)", margin: 0 }}>
            {lang === "de" ? "Mehr zu Live-Rollenspiel: " : "More on live-action roleplay: "}
            <Link to={wasIstLarpPath}>{lang === "de" ? "Was ist LARP?" : "What is LARP?"}</Link>
            {" · "}
            <Link to={eventsPath}>{lang === "de" ? "Unsere Events" : "Our events"}</Link>
            {" · "}
            <Link to={ehrenamtPath}>{lang === "de" ? "Ehrenamt (Teamer werden)" : "Volunteering (become a supervisor)"}</Link>
          </p>
        </div>
          </div>
        </div>
      </div>
    </>
  );
}
